import { observer, useViewModel } from '@descomplica/microfrontend-react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import SimpleSlider from '@/presentation/screens/exercises/components/simple-slider';

import { ExamViewModel } from '../../exam.vm';
import ExamTimer from '../exam-timer';
import Correct from '../icons/Correct.svg';
import Wrong from '../icons/Wrong.svg';
import Questions from '../questions';

const Container = styled.div`
  width: 100%;
  background: #fff;
`;

const StepIndicatorsImage = styled.img`
  text-align: center;
  padding: 0px;
`;

const StepIndicators = styled.div<{ isResult?: boolean }>`
  width: ${(props) => (props.isResult ? '100%' : '85%')};
  height: 100%;
  padding: 2px;
  font-weight: normal;
  color: black;
  gap: 16px;
  padding: 32px 24px 24px 48px;
  border-right: 1px solid #caccce;
  height: 100%;
`;

const StepContainer = styled.div`
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
`;

const QuestionsNumbers = styled.div`
  padding-bottom: 15px;
`;

const QuestionsNumbersItem = styled.span`
  font-size: 20px;
  width: 24px;
  height: 24px;
  line-height: 20px;
  cursor: pointer;
  font-weight: 600;

  & > p {
    padding-top: 3px;
    width: 40px !important;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & > img {
    padding: 0;
    margin-top: 10px;
  }

  &.active {
    border: 1px solid #111;
    border-radius: 50%;
    width: 40px !important;
    height: 40px;
    text-align: center;
    margin-top: 0px;
  }

  &.active p {
    padding: 0px;
  }

  &.active img {
    margin-top: 9px;
    margin-left: 9px;
  }
`;

const QuestionsTitle = styled.p`
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  margin-bottom: 32px;
`;

const ContainerSteps = styled.div`
  display: flex;
  border-bottom: 1px solid #caccce;
  align-items: flex-start;
  height: 100%;
  width: 100%;
`;

const TitleQuestion = styled.h2`
  font-family: 'Aprova Nova';
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 28px;
  letter-spacing: -1px;
  width: 22px;
  color: #111111;
  text-align: center;

  &:after {
    display: block;
    content: '';
    width: 22px;
    height: 4px;
    border-bottom: 4px solid #00e88f;
    position: relative;
    left: 0;
    bottom: 0;
  }
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px 48px 48px 48px;
  gap: 10px;

  width: 100%;
  font-family: 'Aprova Nova';
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 32px;
  color: #666666;
`;

const Button = styled.button<{ showButton?: boolean }>`
  background: #00e88f;
  display: ${(props) => (!props.showButton ? 'none' : 'block')};
  padding: 24px;
  width: 100%;
  font-family: 'Aprova Nova';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  letter-spacing: -0.6px;
  border-radius: 0px 0px 8px 8px;
  color: #111111;
  position: absolute;
  bottom: 0;
  left: 0;

  @media (max-width: 480px) {
    border-radius: 0;
  }
`;

const handleLabelButtonStep = (vm: ExamViewModel, exercises) => {
  if (vm.currentStep + 1 === exercises?.exercisesQuestion.length) {
    return 'Ver resultado';
  }

  return 'Ir para próxima questão';
};

const handleOnClickButton = (vm: ExamViewModel, guid: string, exercises) => {
  if (vm?.currentQuestion?.answered) {
    if (
      vm.currentStep + 1 === exercises.exercisesQuestion.length &&
      vm.answeredExercises.length === exercises.exercisesQuestion.length
    ) {
      vm.setShowResult(true);
    }
    //show related responses
    if (!vm.showResult) {
      vm.setCurrentStep(vm.currentStep + 1);
    } else {
      vm.setCurrentStep(0);
    }
  } else {
    vm.pushAnswersArray(
      {
        exercises_guid: vm.exercisesGuid,
        answers: [
          {
            question_guid: vm?.currentQuestion?.question_guid,
            choices_guid: vm.selectedOption,
          },
        ],
      },
      guid,
    );

    if (vm.answers) localStorage.setItem('answeredQuestions', JSON.stringify(vm.answers));
    if (vm.currentStep + 1 === exercises?.exercisesQuestion.length) {
      vm.postExamAnswerQuestion();
      vm.setCurrentStep(vm.currentStep + 1);
    } else {
      vm.setCurrentStep(vm.currentStep + 1);
      vm.setSelectedOption(null);
      vm.setCurrentQuestion(exercises?.exercisesQuestion[vm.currentStep]);
    }
  }
};

export const MultiStep: React.FC<any> = ({ exercises, resultPage = false }) => {
  const vm = useViewModel(ExamViewModel);
  const navigate = useNavigate();
  const { guid } = useParams();

  useEffect(() => {
    if (exercises) {
      vm.setCurrentQuestion(exercises.exercisesQuestion[vm.currentStep]);
      if (guid) vm.setExercisesGuid(guid);
    } else {
      vm.handleScreenEnter(guid);
    }
  }, [exercises, vm.currentStep]);

  return (
    <Container>
      <ContainerSteps>
        <StepIndicators isResult={resultPage}>
          <StepContainer>
            <QuestionsTitle>Questões</QuestionsTitle>
            <QuestionsNumbers>
              <SimpleSlider>
                {exercises?.exercisesQuestion.map((question, index) => {
                  return (
                    <QuestionsNumbersItem
                      key={Math.random()}
                      onClick={() => {
                        vm.setCurrentStep(index);
                        vm.setCurrentQuestion(exercises.exercisesQuestion[index]);
                        vm.setSelectedOption(null);
                      }}
                      className={index === vm.currentStep ? 'active' : ''}
                    >
                      {question.answered ? (
                        <StepIndicatorsImage src={question.correct ? Correct : Wrong} alt="" />
                      ) : (
                        <p>{index + 1}</p>
                      )}
                    </QuestionsNumbersItem>
                  );
                })}
              </SimpleSlider>
            </QuestionsNumbers>
          </StepContainer>
        </StepIndicators>
        {!resultPage && <ExamTimer minutes={exercises?.examExercisesConfig?.interval_minutes} />}
      </ContainerSteps>
      <StepContent>
        <TitleQuestion>{vm.currentStep + 1}</TitleQuestion>
        <Questions question={vm?.currentQuestion} />
      </StepContent>
      {!resultPage && (
        <Button
          showButton={vm?.currentQuestion?.answered || vm.selectedOption}
          onClick={() => {
            handleOnClickButton(vm, guid, exercises);
            if (vm.currentStep === exercises?.exercisesQuestion.length) {
              window.onbeforeunload = null;
              navigate(`/exercises/confirmation/${guid}`);
            }
          }}
        >
          {handleLabelButtonStep(vm, exercises)}
        </Button>
      )}
    </Container>
  );
};

export default observer(MultiStep);
